import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "@nexus/ui"
import type { CreateCreativeFlagRequest } from "@nexus/contracts"
import { getLocalUserFacingApiMessage } from "@/lib/api-client"
import { useCreateCreativeFlag } from "@/features/opportunities/hooks/useCreativeFlags"

export interface CreateFlagDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  report: Omit<CreateCreativeFlagRequest, "reason">
  onCreated: () => void
}

export function CreateFlagDialog({ open, onOpenChange, report, onCreated }: CreateFlagDialogProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState("")
  const createFlag = useCreateCreativeFlag()

  function handleSubmit() {
    if (!reason.trim()) return
    createFlag.mutate(
      { ...report, reason },
      {
        onSuccess: () => {
          toast.success(t("opportunities.drawer.flagReportSuccess"))
          setReason("")
          onOpenChange(false)
          onCreated()
        },
        onError: (err) => {
          const message = getLocalUserFacingApiMessage(err)
          if (message) toast.error(message)
        },
      },
    )
  }

  function handleCancel() {
    setReason("")
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("opportunities.drawer.flagReportTitle")}</DialogTitle>
        </DialogHeader>

        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("opportunities.drawer.flagReportPlaceholder")}
          rows={4}
        />

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={createFlag.isPending}>
            {t("opportunities.drawer.flagReportCancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason.trim() || createFlag.isPending}
          >
            {t("opportunities.drawer.flagReportSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
